import React, { useEffect, useState } from 'react';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { push } from 'connected-react-router';
import { withStyles } from '@material-ui/core/styles';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Tooltip,
  Typography,
} from '@material-ui/core';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import SearchIcon from '@material-ui/icons/Search';
import TableChartIcon from '@material-ui/icons/TableChart';
import MapIcon from '@material-ui/icons/Map';
import { format, parseISO } from 'date-fns';

import { OpportunityStatusChip, UnderwriteStatusChip } from 'components/layout';
import { TaskStatusChip } from 'components/underwrite/postAcquisition';
import PostAcqUnderwriteStatusChip from 'components/underwrite/postAcquisition/UnderwriteStatusChip';
import {
  fetchIdSearchResults,
  setChannelWorkflowGridStateUpdate,
  setPostAcqWorkflowGridStateUpdate,
  createExploreSystemView,
  appNotify,
} from 'store/actions';
import { Constants } from 'utilities/constants';

const ID_TYPE_OPTIONS = [
  { value: 'opportunityId', label: 'Opportunity/Asset ID' },
  { value: 'taskId', label: 'Task ID' },
  { value: 'asgPropID', label: 'PM ID' },
];

const VALID_ID_TYPES = ID_TYPE_OPTIONS.map(o => o.value);

// An opportunity id and its asset id are the same value, so they share one dropdown option and one
// URL type ('opportunityId'). A legacy/external deep-link using 'assetId' is accepted as an alias —
// the backend returns identical results for either.
const normalizeIdType = idType => (idType === 'assetId' ? 'opportunityId' : idType);

// True when a card field's value is the value the user searched for — used to highlight the matched
// field on each result, mirroring the titlebar search (see SearchCard.highlightSearchString / isSearchedId).
const isSearchedId = (value, searchedId) =>
  value != null && searchedId != null && value.toString() === searchedId.toString();

const styles = theme => ({
  root: {
    padding: theme.spacing(3),
    height: 'calc(100vh - 64px)',
    overflowY: 'auto',
  },
  searchBar: {
    display: 'flex',
    alignItems: 'flex-end',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
    flexWrap: 'wrap',
  },
  idTypeControl: {
    minWidth: 180,
  },
  idInput: {
    minWidth: 220,
  },
  resultsHeader: {
    marginBottom: theme.spacing(2),
  },
  card: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
  cardContent: {
    flexGrow: 1,
    cursor: 'pointer',
  },
  cardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(1),
  },
  acqChip: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
  },
  postAcqChip: {
    backgroundColor: theme.palette.secondary.main,
    color: theme.palette.secondary.contrastText,
  },
  field: {
    marginTop: theme.spacing(0.5),
  },
  fieldLabel: {
    fontWeight: 600,
  },
  statusRow: {
    display: 'flex',
    alignItems: 'center',
    marginTop: theme.spacing(0.75),
  },
  statusLabel: {
    fontWeight: 600,
    fontSize: '0.875rem',
    marginRight: theme.spacing(1),
  },
  addressHeader: {
    marginBottom: theme.spacing(1),
  },
  // Mirrors SearchCard.inlineSearchText (bold + yellow) so the matched value reads the same as the
  // titlebar search highlight.
  matchHighlight: {
    fontWeight: 'bold',
    backgroundColor: 'yellow',
    padding: '0 2px',
    borderRadius: 2,
  },
  cardActions: {
    flexWrap: 'wrap',
  },
  emptyState: {
    marginTop: theme.spacing(4),
    color: theme.palette.text.secondary,
  },
  // Wide, low-height row: each result is one full-width card, stacked vertically.
  resultsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
  },
  rowCard: {
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(1.5, 2),
    gap: theme.spacing(2),
  },
  rowClickable: {
    display: 'flex',
    alignItems: 'center',
    flexGrow: 1,
    flexWrap: 'nowrap',
    columnGap: theme.spacing(3),
    minWidth: 0,
  },
  moduleCell: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: theme.spacing(0.75),
    width: 150,
    flexShrink: 0,
  },
  idsCell: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    width: 140,
    flexShrink: 0,
  },
  idLine: {
    display: 'flex',
    alignItems: 'baseline',
    gap: theme.spacing(0.5),
    fontSize: '0.875rem',
    whiteSpace: 'nowrap',
  },
  idLineLabel: {
    fontWeight: 600,
    fontSize: '0.7rem',
    textTransform: 'uppercase',
    letterSpacing: '0.4px',
    color: theme.palette.text.secondary,
    minWidth: 56,
  },
  cell: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    flexShrink: 0,
  },
  cellLabel: {
    fontWeight: 600,
    fontSize: '0.7rem',
    textTransform: 'uppercase',
    letterSpacing: '0.4px',
    color: theme.palette.text.secondary,
  },
  cellValue: {
    fontSize: '0.875rem',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  rowActions: {
    display: 'flex',
    flexShrink: 0,
    flexWrap: 'wrap',
    justifyContent: 'flex-end',
  },
});

const IdSearch = ({
  classes,
  location,
  history,
  results,
  isSearching,
  hasSearched,
  fetchIdSearchResults,
  push,
  setChannelWorkflowGridStateUpdate,
  setPostAcqWorkflowGridStateUpdate,
  createExploreSystemView,
  appNotify,
}) => {
  const [idType, setIdType] = useState('opportunityId');
  const [idValue, setIdValue] = useState('');

  // Drive the search entirely from the URL query string. This handles both a manual search
  // (the Search button pushes ?idType=&id=) and an external deep-link (a cold load with the
  // params already present), and keeps the two paths from diverging.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const urlIdType = normalizeIdType(params.get('idType'));
    const urlId = params.get('id');

    if (urlIdType && VALID_ID_TYPES.includes(urlIdType)) {
      setIdType(urlIdType);
    }
    if (urlId != null) {
      setIdValue(urlId);
    }
    if (urlIdType && VALID_ID_TYPES.includes(urlIdType) && urlId && urlId.trim() !== '') {
      fetchIdSearchResults(urlIdType, urlId.trim());
    }
  }, [location.search, fetchIdSearchResults]);

  const handleSearch = () => {
    const trimmed = idValue.trim();
    if (trimmed === '') {
      appNotify('Please enter an ID to search for.', 'warning');
      return;
    }
    history.push({
      pathname: Constants.Routes.DASHBOARD_SEARCH,
      search: `?idType=${encodeURIComponent(idType)}&id=${encodeURIComponent(trimmed)}`,
    });
  };

  const handleKeyPress = e => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const getUnderwriteRoute = result =>
    result.module === 'Acq' ? Constants.Routes.UNDERWRITE_PROPERTY : Constants.Routes.POSTACQ_TASK;

  const canOpen = result => result.module === 'Acq' || result.canOpenUnderwrite;

  const openUnderwrite = (result, newTab) => {
    // Unloaded post-acq tasks aren't openable; the card click and New Tab button are both disabled for
    // them (with an explanatory tooltip), so this is just a defensive no-op.
    if (!canOpen(result)) return;
    const route = `${getUnderwriteRoute(result)}/${result.id}`;
    if (newTab) {
      window.open(`${window.location.origin}${route}`, '_blank');
    } else {
      push(route);
    }
  };

  const viewOnWorkflow = result => {
    if (result.module === 'Acq') {
      setChannelWorkflowGridStateUpdate(
        {
          filterValue: [['id', '=', result.id]],
          columnMods: [{ field: 'id', type: 'sortOrder', value: 'asc' }],
        },
        true
      );
      push(Constants.Routes.CHANNEL_WORKFLOW);
    } else {
      setPostAcqWorkflowGridStateUpdate({ filterValue: [['id', '=', result.id]] }, true);
      push(Constants.Routes.POSTACQ_WORKFLOW);
    }
  };

  const viewOnExplore = result => {
    if (!result.asgPropID) {
      appNotify('This result has no property id, so it cannot be shown on Explore.', 'warning');
      return;
    }
    const filterValue = ['asgPropID', '=', result.asgPropID];
    const filterExpression = [['asgPropID', '=', result.asgPropID]];
    createExploreSystemView(`Search asgPropID ${result.asgPropID}`, { filterValue, filterExpression });
    push(Constants.Routes.EXPLORE);
  };

  const formatDate = value => {
    if (!value) return '—';
    try {
      return format(parseISO(value), 'MM/dd/yyyy');
    } catch {
      return '—';
    }
  };

  // The value the current results were matched against comes from the URL (the same source that drives
  // the search), so it always lines up with what's displayed.
  const searchedId = new URLSearchParams(location.search).get('id');

  // A labeled column cell (label above value) — the building block of the wide row layout.
  // A fixed width keeps the same field aligned vertically down the results list.
  const renderCell = (label, node, width) => (
    <div className={classes.cell} style={{ width }}>
      <span className={classes.cellLabel}>{label}</span>
      <div className={classes.cellValue}>{node ?? '—'}</div>
    </div>
  );

  // Highlights a value when it's the one the search matched on (mirrors the titlebar search).
  const highlightId = value => (
    <span className={isSearchedId(value, searchedId) ? classes.matchHighlight : undefined}>{value}</span>
  );

  // One "LABEL value" line inside the stacked IDs cell.
  const renderIdLine = (label, value) => (
    <div className={classes.idLine}>
      <span className={classes.idLineLabel}>{label}</span>
      {highlightId(value)}
    </div>
  );

  const formatAddress = result => {
    if (!result) return null;
    const cityStateZip = [result.city, [result.state, result.zipCode].filter(Boolean).join(' ')]
      .filter(Boolean)
      .join(', ');
    return [result.street, cityStateZip].filter(Boolean).join(', ') || null;
  };

  // Reuse the exact status chips from the workflow grids / UW pages so colors stay consistent.
  // The chips read a task/opportunity-shaped object; build a minimal one from the result row.
  const moduleStatusChip = result => {
    if (result.module === 'Acq') {
      return (
        <OpportunityStatusChip
          txnOpp={{
            opportunityStatus: result.status,
            underwriteStatus: result.underwriteStatus,
            userId: result.assignedUserId,
          }}
        />
      );
    }
    return (
      <TaskStatusChip
        task={{
          taskStatus: result.status,
          underwriteStatus: result.underwriteStatus,
          consumedOn: result.consumedOn,
          assignedUser: result.assignedUser,
          postAcquisitionLoadCompletedOn: result.canOpenUnderwrite || null,
        }}
      />
    );
  };

  const uwStatusChip = result => {
    if (!result.underwriteStatus) return '—';
    if (result.module === 'Acq') {
      return <UnderwriteStatusChip underwriteStatus={result.underwriteStatus} />;
    }
    return (
      <PostAcqUnderwriteStatusChip
        task={{ underwriteStatus: result.underwriteStatus, consumedOn: result.consumedOn }}
      />
    );
  };

  const renderCard = (result, index) => {
    const isAcq = result.module === 'Acq';
    const openable = canOpen(result);
    const rowContent = (
      <div
        className={classes.rowClickable}
        style={openable ? { cursor: 'pointer' } : { cursor: 'default' }}
        onClick={openable ? () => openUnderwrite(result, false) : undefined}
      >
        <div className={classes.moduleCell}>
          <Chip
            size="small"
            label={isAcq ? 'Acquisitions' : 'Post-Acquisitions'}
            className={isAcq ? classes.acqChip : classes.postAcqChip}
          />
          {renderCell('Type', result.type, '100%')}
        </div>
        <div className={classes.idsCell}>
          {renderIdLine(isAcq ? 'Opp ID' : 'Task ID', result.id)}
          {result.asgPropID != null && renderIdLine('PM ID', result.asgPropID)}
          {result.assetId != null && renderIdLine('Asset ID', result.assetId)}
        </div>
        {renderCell('Status', moduleStatusChip(result), 104)}
        {renderCell('UW Status', uwStatusChip(result), 150)}
        {renderCell('Saved By', result.user, 160)}
        {renderCell('Created On', formatDate(result.createdOn), 110)}
      </div>
    );

    return (
      <Paper className={classes.rowCard} key={`${result.module}-${result.id}-${index}`}>
        {openable ? (
          <Tooltip title="Open underwrite page">{rowContent}</Tooltip>
        ) : (
          <Tooltip title="Task data must be loaded before its underwrite page can be opened.">
            {rowContent}
          </Tooltip>
        )}
        <div className={classes.rowActions}>
          <Tooltip title="Open underwrite page in a new tab">
            <span>
              <IconButton
                disabled={!openable}
                onClick={() => openUnderwrite(result, true)}
              >
                <OpenInNewIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="View on Workflow">
            <IconButton onClick={() => viewOnWorkflow(result)}>
              <TableChartIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={result.asgPropID ? 'View on Explore' : 'No property id available'}>
            <span>
              <IconButton
                disabled={!result.asgPropID}
                onClick={() => viewOnExplore(result)}
              >
                <MapIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </div>
      </Paper>
    );
  };

  return (
    <div className={classes.root}>
      <Typography variant="h5" gutterBottom>
        Search
      </Typography>
      <Typography variant="body2" style={{ marginBottom: 16 }}>
        Locate an acquisition opportunity or post-acquisition task by ID.
      </Typography>

      <div className={classes.searchBar}>
        <FormControl className={classes.idTypeControl}>
          <InputLabel id="id-search-type-label">ID Type</InputLabel>
          <Select
            labelId="id-search-type-label"
            value={idType}
            onChange={e => setIdType(e.target.value)}
          >
            {ID_TYPE_OPTIONS.map(option => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          className={classes.idInput}
          label="ID"
          value={idValue}
          onChange={e => setIdValue(e.target.value)}
          onKeyPress={handleKeyPress}
          onFocus={e => e.target.select()}
        />
        <Button
          variant="contained"
          color="primary"
          startIcon={<SearchIcon />}
          onClick={handleSearch}
          disabled={isSearching}
        >
          Search
        </Button>
      </div>

      {isSearching && (
        <Box display="flex" alignItems="center" justifyContent="center" mt={4}>
          <CircularProgress />
        </Box>
      )}

      {!isSearching && hasSearched && (
        <>
          {formatAddress(results.find(r => r.street)) && (
            <Typography variant="h6" className={classes.addressHeader}>
              {formatAddress(results.find(r => r.street))}
            </Typography>
          )}
          <Typography variant="subtitle1" className={classes.resultsHeader}>
            {results.length} result{results.length === 1 ? '' : 's'}
          </Typography>
          {results.length === 0 ? (
            <Typography className={classes.emptyState}>
              No opportunities or tasks found for that ID.
            </Typography>
          ) : (
            <div className={classes.resultsList}>
              {results.map(renderCard)}
            </div>
          )}
        </>
      )}
    </div>
  );
};

const mapStateToProps = state => ({
  results: state.dashboardState.idSearch.results,
  isSearching: state.dashboardState.idSearch.isSearching,
  hasSearched: state.dashboardState.idSearch.hasSearched,
});

const mapDispatchToProps = {
  fetchIdSearchResults,
  push,
  setChannelWorkflowGridStateUpdate,
  setPostAcqWorkflowGridStateUpdate,
  createExploreSystemView,
  appNotify,
};

export default compose(
  withRouter,
  withStyles(styles),
  connect(mapStateToProps, mapDispatchToProps)
)(IdSearch);
